import { useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import { compressImage } from '../lib/imageUtils';
import { enqueueOp, putPendingBlob, deletePendingBlob } from '../lib/db';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db as firestore, storage } from '../firebase/firebaseConfig';

export function usePhotos() {
  const { user, isOnline, upsertPhotoState, removePhotoState, derived, activeHunt } = useAppContext();
  const { photosByItem } = derived;

  const addPhoto = useCallback(async ({ itemKey, location, file, filePath, precompressed }) => {
    const id = crypto.randomUUID();
    const existingForItem = photosByItem.get(itemKey) ?? [];
    const isFirst = existingForItem.length === 0;

    let thumbnailDataUrl = null;
    let thumbnailBlob    = null;
    let qualityScore     = null;

    if (precompressed) {
      thumbnailDataUrl = precompressed.dataUrl;
      thumbnailBlob    = precompressed.blob;
      qualityScore     = precompressed.qualityScore ?? null;
    } else if (file) {
      const compressed = await compressImage(file);
      thumbnailDataUrl = compressed.dataUrl;
      thumbnailBlob    = compressed.blob;
      qualityScore     = compressed.qualityScore ?? null;
    }

    const photo = {
      id,
      huntId:           activeHunt.id,
      itemKey:          String(itemKey),
      location:         location.trim(),
      filePath:         filePath?.trim() || null,
      thumbnailUrl:     '',
      thumbnailDataUrl,
      qualityScore,
      addedAt:          new Date().toISOString(),
      isPrimary:        isFirst,
    };

    // Save to IDB immediately
    await upsertPhotoState(photo);

    // If online and authenticated, upload to Firebase
    if (isOnline && user) {
      try {
        let thumbnailUrl = '';
        if (thumbnailBlob) {
          const storageRef = ref(storage, `users/${user.uid}/thumbnails/${id}.jpg`);
          await uploadBytes(storageRef, thumbnailBlob);
          thumbnailUrl = await getDownloadURL(storageRef);
        }

        const firestorePhoto = { ...photo, thumbnailUrl, thumbnailDataUrl: null };
        await setDoc(doc(firestore, 'users', user.uid, 'photos', id), firestorePhoto);

        // Update local copy with the real thumbnailUrl
        const updatedPhoto = { ...photo, thumbnailUrl };
        await upsertPhotoState(updatedPhoto);
      } catch (err) {
        console.error('Firebase upload failed, queuing for later:', err);
        if (thumbnailBlob) await putPendingBlob(id, thumbnailBlob);
        await enqueueOp('set', { userId: user.uid, photo });
      }
    } else if (user) {
      // Signed in but offline — queue it, and stash the blob so the queue
      // flush can actually upload it later (it can't survive in the queue
      // payload itself across a reload)
      if (thumbnailBlob) await putPendingBlob(id, thumbnailBlob);
      await enqueueOp('set', { userId: user.uid, photo });
    }
    // If not signed in at all, just stays in IDB

    return photo;
  }, [user, isOnline, upsertPhotoState, photosByItem, activeHunt]);

  const updatePhoto = useCallback(async (id, changes) => {
    // Find photo across all items in the active hunt
    let existing = null;
    for (const [, photos] of photosByItem) {
      existing = photos.find(p => p.id === id);
      if (existing) break;
    }
    if (!existing) return;

    const updated = { ...existing, ...changes };
    await upsertPhotoState(updated);

    if (isOnline && user) {
      try {
        const firestorePhoto = { ...updated, thumbnailDataUrl: null };
        await setDoc(doc(firestore, 'users', user.uid, 'photos', id), firestorePhoto);
      } catch (err) {
        await enqueueOp('set', { userId: user.uid, photo: updated });
      }
    } else if (user) {
      await enqueueOp('set', { userId: user.uid, photo: updated });
    }

    return updated;
  }, [user, isOnline, upsertPhotoState, photosByItem]);

  const setPrimary = useCallback(async (itemKey, photoId) => {
    const photos = photosByItem.get(itemKey) ?? [];
    for (const p of photos) {
      await updatePhoto(p.id, { isPrimary: p.id === photoId });
    }
  }, [photosByItem, updatePhoto]);

  const deletePhoto = useCallback(async (id, itemKey) => {
    await removePhotoState(id);
    await deletePendingBlob(id);

    if (isOnline && user) {
      try {
        await deleteDoc(doc(firestore, 'users', user.uid, 'photos', id));
        const storageRef = ref(storage, `users/${user.uid}/thumbnails/${id}.jpg`);
        await deleteObject(storageRef).catch(() => {}); // ignore not-found
      } catch (err) {
        await enqueueOp('delete', { userId: user.uid, photoId: id });
      }
    } else if (user) {
      await enqueueOp('delete', { userId: user.uid, photoId: id });
    }

    // If the deleted photo was primary, promote the next one
    const remaining = (photosByItem.get(itemKey) ?? []).filter(p => p.id !== id);
    if (remaining.length > 0 && !remaining.some(p => p.isPrimary)) {
      await updatePhoto(remaining[0].id, { isPrimary: true });
    }
  }, [user, isOnline, removePhotoState, photosByItem, updatePhoto]);

  return { addPhoto, updatePhoto, setPrimary, deletePhoto };
}
